import { useCallback, useEffect } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { fetchAccountOnboardingActions } from 'api/account';
import {
    IRecommendedAction,
    MAX_TIME_SINCE_ACTION_COMPLETED,
    actions,
} from 'components/recommended-actions/constants';
import { DOWNLOAD_MOBILE_GOOGLE_STORE, DOWNLOAD_MOBILE_IOS_STORE } from 'constants/links';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { getOS } from 'helpers/os';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';

interface IRecommendedActionWithState extends IRecommendedAction {
    isComplete: boolean;
}

const useRecommendedActionsData = () => {
    const { accountId } = useCurrentAccount();
    const {
        hideRecommendedActionsKey: hideKey,
        setCompletableRecommendedActionsKeys: setCompletableKeys,
        setHasSeenCompleteAllRecommendedActionsDialog,
    } = useAccountLocalStorage(
        ({
            hideRecommendedActionsKey,
            setCompletableRecommendedActionsKeys,
            setHasSeenCompleteAllRecommendedActionsDialog,
        }) => ({
            hideRecommendedActionsKey,
            setCompletableRecommendedActionsKeys,
            setHasSeenCompleteAllRecommendedActionsDialog,
        }),
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.recommendedActions(accountId),
        queryFn: () => fetchAccountOnboardingActions(accountId),
        select: data => {
            const recommendedActions = data
                .filter(action => action.action_item && action.action_item in actions) // filter out invalid or not-yet defined actions
                .filter(action => !action.is_required);

            const keysToHide: string[] = [];
            const keysToAnimateCompletion: string[] = [];

            const actionData = recommendedActions.map(action => {
                const actionItem = {
                    ...actions[action.action_item],
                    isComplete: !!action.completed_at,
                };

                if (actionItem.actionKey === 'downloadMobileApp') {
                    const os = getOS();
                    actionItem.getStartedLink =
                        os === 'android' ? DOWNLOAD_MOBILE_GOOGLE_STORE : DOWNLOAD_MOBILE_IOS_STORE;
                } else {
                    actionItem.getStartedLink = `/accounts/${accountId}/${actionItem.getStartedLink}`;
                }

                if (action.completed_at) {
                    if (
                        isoDateFromServer(action.completed_at) <
                        DateTime.now().minus(MAX_TIME_SINCE_ACTION_COMPLETED)
                    ) {
                        keysToHide.push(actionItem.actionKey);
                    } else {
                        keysToAnimateCompletion.push(actionItem.actionKey);
                    }
                }

                return actionItem;
            });

            return {
                data: recommendedActions,
                actionData,
                keysToHide,
                keysToAnimateCompletion,
            };
        },
        refetchOnWindowFocus: 'always',
    });

    useEffect(() => {
        for (const key of data.keysToHide) {
            hideKey(key);
        }
    }, [data.keysToHide, hideKey]);

    useEffect(() => {
        setCompletableKeys(data.keysToAnimateCompletion);
    }, [data.keysToAnimateCompletion, setCompletableKeys]);

    useEffect(() => {
        if (areAllActionsStale(data.data)) {
            setHasSeenCompleteAllRecommendedActionsDialog(true);
        }
    }, [data.data, setHasSeenCompleteAllRecommendedActionsDialog]);

    return data;
};

const useInvalidateRecommendedActionsQuery = () => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const invalidateRecommendedActionsQuery = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.recommendedActions(accountId),
        });
    }, [queryClient, accountId]);

    return {
        invalidateRecommendedActionsQuery,
    };
};

const areAllActionsStale = (data: ReturnType<typeof useRecommendedActionsData>['data']) => {
    const completedTimestamps = data
        .map(action => action.completed_at)
        .filter(completedAt => completedAt !== null);

    if (data.length !== completedTimestamps.length) {
        return false;
    }

    const mostRecentCompletion = completedTimestamps.reduce((prev, curr) => {
        return new Date(curr) > new Date(prev) ? curr : prev;
    }, completedTimestamps[0]);

    return (
        isoDateFromServer(mostRecentCompletion) <
        DateTime.now().minus(MAX_TIME_SINCE_ACTION_COMPLETED)
    );
};

export { useRecommendedActionsData, useInvalidateRecommendedActionsQuery };
export type { IRecommendedActionWithState };
